import React, { createContext, useContext, useEffect } from "react";
import { useAccessibilityPreferences } from "../store/userPreferencesStore";

interface AccessibilityContextType {
  accessibility: {
    reducedMotion: boolean;
    fontSize: "small" | "medium" | "large";
    screenReader: boolean;
    keyboardNavigation: boolean;
  };
  updateAccessibility: (
    accessibility: Partial<{
      reducedMotion: boolean;
      fontSize: "small" | "medium" | "large";
      screenReader: boolean;
      keyboardNavigation: boolean;
    }>
  ) => void;
}

const AccessibilityContext = createContext<
  AccessibilityContextType | undefined
>(undefined);

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({
  children,
}) => {
  const { accessibility, updateAccessibility } = useAccessibilityPreferences();

  useEffect(() => {
    // Apply accessibility settings to document
    const root = document.documentElement;

    // Reduced motion
    if (accessibility.reducedMotion) {
      root.classList.add("reduce-motion");
    } else {
      root.classList.remove("reduce-motion");
    }

    // Font size
    root.classList.remove("font-small", "font-medium", "font-large");
    root.classList.add(`font-${accessibility.fontSize}`);

    // Screen reader optimizations
    if (accessibility.screenReader) {
      root.classList.add("screen-reader-optimized");
    } else {
      root.classList.remove("screen-reader-optimized");
    }
  }, [accessibility]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Alt+M: Skip to main content
      if (event.altKey && event.key.toLowerCase() === "m") {
        event.preventDefault();
        const mainContent =
          document.getElementById("main-content") ||
          document.getElementById("main-form") ||
          document.querySelector("main");
        if (mainContent) {
          mainContent.focus();
          mainContent.scrollIntoView({ behavior: "smooth" });
        }
      }

      // Alt+R: Toggle reduced motion
      if (event.altKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        updateAccessibility({ reducedMotion: !accessibility.reducedMotion });
      }
    };

    if (accessibility.keyboardNavigation) {
      document.addEventListener("keydown", handleKeyPress);
      return () => document.removeEventListener("keydown", handleKeyPress);
    }
  }, [accessibility, updateAccessibility]);

  return (
    <AccessibilityContext.Provider
      value={{ accessibility, updateAccessibility }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error(
      "useAccessibility must be used within an AccessibilityProvider"
    );
  }
  return context;
};
